// Both Product Feedback modals are MUI Dialogs, and Dialog's Paper follows
// palette.mode — so it goes dark navy while this palette stayed light, putting
// near-black ink on a dark surface. Pointing each entry at the theme token for
// its ROLE fixes both modals from this one file: every consumer is a plain CSS
// value (sx props and `1px solid ${...}` shorthands), and CSS custom properties
// resolve fine in all of them. See src/theme/tokens.ts for the values.
//
// The comment on each line is the ORIGINAL literal, so light-mode drift is
// auditable: the ones marked "exact" are byte-identical to the token's light
// value; the rest move by a hair within the same family.
export const PRODUCT_TESTING_TOKENS = {
    ink: "var(--text)", //          was #1C1A17 — exact
    muted: "var(--muted)", //       was #9B958C — exact
    sub: "var(--text-2)", //        was #7A7268
    rule: "var(--track)", //        was #EDEAE5 — exact
    border: "var(--border)", //     was #E5E1DB
    gold: "var(--accent)", //       was #A8843E
    goldSoft: "var(--accent-dark)", // was #C8A97E
    goldInk: "var(--accent-text)", // was #A07B2E — exact
    goldTile: "var(--tint)", //     was #FAF2E2
    pendingInk: "var(--accent-text)", // was #8A6D1E — gold family carries "in progress"
    pendingBg: "var(--tint)", //    was #FAEEDA
    doneInk: "var(--up)", //        was #085041 — exact
    doneBg: "var(--up-bg)", //      was #E1F5EE — exact
    filledBorder: "var(--up-bg)", //  was #DCEDE5
    filledBg: "var(--surface)", //  was #FCFEFD
    barBg: "var(--surface-2)", //   was #FAFAF8
    placeholder: "var(--muted)", // was #B5AFA5
    badgeBg: "var(--surface-2)", // was #F4F2EE — exact

    // Deliberately NOT tokenized. These three are solid mid-tone FILLS that
    // carry white text or sit as dots on either canvas, so they already work in
    // both themes — and the obvious token is wrong for each: --up in dark is a
    // bright #3CD670 that would drop the badge's white numeral to ~1.9:1, and
    // there is no amber token at all.
    goldHover: "#967435",
    pendingDot: "#C8973A",
    doneDot: "#1C7A5A",
};

interface ProductTestingQuestion {
    field: string;
    group: string;
    label: string;
    hint?: string;
}

const PRODUCT_TESTING_QUESTIONS: ProductTestingQuestion[] = [
    {
        field: "likes_most",
        group: "Product Experience",
        label: "What do you like most about this product?",
    },
    {
        field: "personal_professional_benefit",
        group: "Product Experience",
        label: "How has this product benefited you personally and/or professionally?",
    },
    {
        field: "made_life_easier",
        group: "Product Experience",
        label: "In what ways has this product made your professional and/or personal life easier?",
    },
    {
        field: "comparison_to_similar",
        group: "Product Experience",
        label: "How does this product compare to similar products you have used?",
    },
    {
        field: "most_valuable_features",
        group: "Product Experience",
        label: "Which features or functionalities do you find most valuable?",
    },
    {
        field: "brand_standout",
        group: "Product Experience",
        label: "What stands out to you most about the brand?",
    },
    {
        field: "client_patient_benefit",
        group: "Client / Patient Impact",
        label: "Do you believe your clients/patients could benefit from this product? If so, how?",
    },
    {
        field: "recommendation",
        group: "Recommendation",
        label: "Who would you recommend this product to and why?",
    },
    {
        field: "additional_feedback",
        group: "Additional Feedback",
        label: "Please share any additional thoughts or feedback about your experience.",
        hint: "This may include features you particularly appreciate, unique ways the product has helped you, or anything else you'd like others to know.",
    },
];

interface ProductTestingSection {
    title: string;
    num: string;
    questions: ProductTestingQuestion[];
}

export const PRODUCT_TESTING_SECTIONS: ProductTestingSection[] =
    PRODUCT_TESTING_QUESTIONS.reduce((acc: ProductTestingSection[], question) => {
        const existing = acc.find((section) => section.title === question.group);
        if (existing) {
            existing.questions.push(question);
            return acc;
        }
        acc.push({
            title: question.group,
            num: String(acc.length + 1).padStart(2, "0"),
            questions: [question],
        });
        return acc;
    }, []);

export const PRODUCT_TESTING_TOTAL = PRODUCT_TESTING_QUESTIONS.length;

export interface ProductTestingAnswers {
    [field: string]: string;
}

export interface ProductTestingForm {
    desireid: number;
    product: { desireid: number; title?: string; image?: string };
    submitted_at: string | null;
    answers: ProductTestingAnswers;
}

export const answeredCount = (answers: ProductTestingAnswers) =>
    PRODUCT_TESTING_QUESTIONS.filter(
        (question) => (answers[question.field] || "").trim().length > 0
    ).length;

// The API's control() wrapper serializes ApiError codes as a BARE JSON string
// (res.json('SomeCode')), not { error: 'SomeCode' } — read both shapes so
// code→message maps actually match.
export const apiErrorCode = (error: any): string => {
    const data = error?.response?.data;
    if (typeof data === "string") return data;
    return (data && data.error) || "";
};
